import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Eye, Edit, Ban, CheckCircle, Calendar } from 'lucide-react';
import { Button, AlertContainer, useAlerts, ConfirmDialog } from '@/components/ui';
import { ClientModal, SubscriptionModal } from '@/components/modals';
import { clientsApi, subscriptionsApi, plansApi, type Plan } from '@/services/api';

interface Client {
  id?: string;
  name: string;
  email: string;
  document: string;
  type: 'individual' | 'company';
  status: 'active' | 'suspended' | 'cancelled';
  createdAt?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface Subscription {
  id?: string;
  clientId: string;
  clientName: string;
  planId: string;
  planName: string;
  planPrice: number;
  status: 'active' | 'suspended' | 'cancelled' | 'pending';
  startDate: string;
  endDate?: string;
  billingCycle: 'monthly' | 'yearly';
  autoRenew: boolean;
  companiesCount: number;
  usersCount: number;
  dueDay?: number;
  paymentMethod?: string;
}

export default function ClientsSubscriptions() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'clients' | 'subscriptions'>('clients');
  const [searchTerm, setSearchTerm] = useState('');
  const [showClientModal, setShowClientModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [viewingClient, setViewingClient] = useState<Client | null>(null);
  const [viewingSubscription, setViewingSubscription] = useState<Subscription | null>(null);
  
  // Sistema de alertas
  const { alerts, showSuccess, showError, removeAlert } = useAlerts();
  
  // Modal de confirmação
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText?: string;
    confirmVariant?: 'primary' | 'danger';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Carregar clientes, assinaturas e planos da API
  useEffect(() => {
    loadClients();
    loadSubscriptions();
    loadPlans();
  }, []);

  const loadClients = async () => {
    try {
      setError(null);
      const data = await clientsApi.getAll();
      setClients(data.map(item => ({
        id: item.id,
        name: item.name,
        email: item.email,
        document: item.document,
        type: item.type,
        status: item.status,
        createdAt: item.createdAt
      })));
    } catch (err: any) {
      console.error('Erro ao carregar clientes:', err);
      console.error('Detalhes do erro:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.message || err.message || 'Erro ao carregar clientes');
      // Manter dados mockados em caso de erro para demonstração
      setClients([
    {
      id: '1',
      name: 'TechStartup Ltda',
      email: 'contato@techstartup.com',
      document: '12.345.678/0001-90',
      type: 'company',
      status: 'active',
      createdAt: '2025-01-15',
    },
    {
      id: '2',
      name: 'João Silva',
      email: 'joao@email.com',
      document: '123.456.789-00',
      type: 'individual',
      status: 'active',
      createdAt: '2025-01-10',
    },
    {
      id: '3',
      name: 'InnovaCorp S.A.',
      email: 'financeiro@innovacorp.com',
      document: '98.765.432/0001-10',
      type: 'company',
      status: 'suspended',
      createdAt: '2024-12-20',
    },
      ]);
    }
  };

  const loadSubscriptions = async () => {
    try {
      setError(null);
      const data = await subscriptionsApi.getAll();
      setSubscriptions(data.map(item => ({
        id: item.id,
        clientId: item.clientId,
        clientName: item.clientName,
        planId: item.planId,
        planName: item.planName,
        planPrice: item.planPrice,
        status: item.status,
        startDate: item.startDate,
        endDate: item.endDate,
        billingCycle: item.billingCycle,
        autoRenew: item.autoRenew,
        companiesCount: item.companiesCount,
        usersCount: item.usersCount,
        dueDay: item.dueDay ?? 1,
        paymentMethod: item.paymentMethod ?? 'pix'
      })));
    } catch (err: any) {
      console.error('Erro ao carregar assinaturas:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar assinaturas');
      // Manter dados mockados em caso de erro para demonstração
      setSubscriptions([
        {
          id: '1',
          clientId: '1',
          clientName: 'TechStartup Ltda',
          planId: '2',
          planName: 'Professional',
          planPrice: 299.00,
          status: 'active',
          startDate: '2025-01-15',
          billingCycle: 'monthly',
          autoRenew: true,
          companiesCount: 2,
          usersCount: 12,
          dueDay: 10,
          paymentMethod: 'pix'
        },
      ]);
    }
  };

  const loadPlans = async () => {
    try {
      const data = await plansApi.getActive();
      setPlans(data);
    } catch (err: any) {
      console.error('Erro ao carregar planos:', err);
      // Fallback para planos mockados
      setPlans([
        { id: '1', name: 'Starter', description: '', price: 99.00, billingCycle: 'monthly', maxCompanies: 1, maxUsers: 5, isActive: true },
        { id: '2', name: 'Professional', description: '', price: 299.00, billingCycle: 'monthly', maxCompanies: 3, maxUsers: 20, isActive: true },
        { id: '3', name: 'Enterprise', description: '', price: 999.00, billingCycle: 'monthly', maxCompanies: -1, maxUsers: -1, isActive: true },
      ]);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      active: 'badge-success',
      suspended: 'badge-warning',
      cancelled: 'badge-error',
      pending: 'badge-info',
    };
    const labels: Record<string, string> = {
      active: t('billing.statusActive'),
      suspended: t('billing.statusSuspended'),
      cancelled: t('billing.statusCancelled'),
      pending: t('billing.statusPending'),
    };
    return <span className={`badge ${variants[status]}`}>{labels[status]}</span>;
  };

  const handleSaveClient = async (client: Client) => {
    try {
      if (client.id) {
        await clientsApi.update(client.id, client);
        showSuccess(t('billing.clientUpdatedSuccess', { name: client.name }));
      } else {
        await clientsApi.create(client);
        showSuccess(t('billing.clientCreatedSuccess', { name: client.name }));
      }
      await loadClients();
      setShowClientModal(false);
      setEditingClient(null);
    } catch (err: any) {
      console.error('Erro ao salvar cliente:', err);
      showError(err.response?.data?.message || t('billing.clientSaveError'));
    }
  };

  const handleSaveSubscription = async (subscription: Subscription) => {
    try {
      console.log('Salvando assinatura:', subscription);
      const dueDay = subscription.dueDay ?? 1;
      const paymentMethod = subscription.paymentMethod ?? 'pix';
      if (subscription.id) {
        // Atualizar
        const updateData = {
          planId: subscription.planId,
          autoRenew: subscription.autoRenew,
          companiesCount: subscription.companiesCount,
          usersCount: subscription.usersCount,
          startDate: subscription.startDate,
          endDate: subscription.endDate || undefined,
          dueDay,
          paymentMethod
        };
        console.log('Dados de atualização:', updateData);
        await subscriptionsApi.update(subscription.id, updateData);
        showSuccess(t('billing.subscriptionUpdatedSuccess'));
      } else {
        await subscriptionsApi.create({
          clientId: subscription.clientId,
          planId: subscription.planId,
          autoRenew: subscription.autoRenew,
          dueDay,
          paymentMethod
        });
        showSuccess(t('billing.subscriptionCreatedSuccess'));
      }
      await loadSubscriptions();
      setShowSubscriptionModal(false);
      setEditingSubscription(null);
    } catch (err: any) {
      console.error('Erro ao salvar assinatura:', err);
      showError(err.response?.data?.message || t('billing.subscriptionSaveError'));
    }
  };

  const handleEditClient = async (client: Client) => {
    try {
      // Carregar dados completos do cliente
      const fullClient = await clientsApi.getById(client.id!);
      setEditingClient(fullClient);
      setShowClientModal(true);
    } catch (err: any) {
      console.error('Erro ao carregar cliente:', err);
      showError(t('billing.clientLoadError'));
    }
  };

  const handleViewClient = async (client: Client) => {
    try {
      const fullClient = await clientsApi.getById(client.id!);
      setViewingClient(fullClient);
    } catch (err: any) {
      console.error('Erro ao carregar cliente:', err);
      showError(t('billing.clientLoadError'));
    }
  };

  const handleNewClient = () => {
    setEditingClient(null);
    setShowClientModal(true);
  };

  const handleEditSubscription = (subscription: Subscription) => {
    setEditingSubscription(subscription);
    setShowSubscriptionModal(true);
  };

  const handleViewSubscription = (subscription: Subscription) => {
    setViewingSubscription(subscription);
  };

  const handleSuspendSubscription = async (subscriptionId?: string) => {
    if (!subscriptionId) return;
    const subscription = subscriptions.find(s => s.id === subscriptionId);
    
    setConfirmDialog({
      isOpen: true,
      title: t('billing.suspendSubscription'),
      message: t('billing.suspendSubscriptionConfirm', { name: subscription?.clientName }),
      confirmText: t('billing.yesSuspend'),
      confirmVariant: 'danger',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await subscriptionsApi.suspend(subscriptionId);
          await loadSubscriptions();
          showSuccess(t('billing.subscriptionSuspendedSuccess'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (err: any) {
          console.error('Erro ao suspender assinatura:', err);
          showError(err.response?.data?.message || t('billing.subscriptionSuspendError'));
        } finally {
          setConfirmLoading(false);
        }
      },
    });
  };

  const handleActivateSubscription = async (subscriptionId?: string) => {
    if (!subscriptionId) return;
    const subscription = subscriptions.find(s => s.id === subscriptionId);
    
    setConfirmDialog({
      isOpen: true,
      title: t('billing.activateSubscription'),
      message: t('billing.activateSubscriptionConfirm', { name: subscription?.clientName }),
      confirmText: t('billing.yesActivate'),
      confirmVariant: 'primary',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await subscriptionsApi.activate(subscriptionId);
          await loadSubscriptions();
          showSuccess(t('billing.subscriptionActivatedSuccess'));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        } catch (err: any) {
          console.error('Erro ao ativar assinatura:', err);
          showError(err.response?.data?.message || t('billing.subscriptionActivateError'));
        } finally {
          setConfirmLoading(false);
        }
      },
    });
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.document.includes(searchTerm)
  );

  const filteredSubscriptions = subscriptions.filter(sub =>
    sub.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.planName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="page-title">Billing</h1>
          <p className="page-subtitle">{t('billing.clientsSubscriptionsPageSubtitle')}</p>
        </div>
        <Button onClick={activeTab === 'clients' ? handleNewClient : () => setShowSubscriptionModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {activeTab === 'clients' ? t('billing.newClient') : t('billing.newSubscription')}
        </Button>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border pb-0">
        <Link to="/billing" className="px-4 py-3 font-medium text-muted-foreground hover:text-foreground transition-colors">
          {t('billing.dashboard')}
        </Link>
        <Link to="/billing/plans" className="px-4 py-3 font-medium text-muted-foreground hover:text-foreground transition-colors">
          {t('billing.plans')}
        </Link>
        <Link to="/billing/clients" className="px-4 py-3 font-medium text-primary border-b-2 border-primary">
          {t('billing.clientsSubscriptions')}
        </Link>
        <Link to="/billing/invoices" className="px-4 py-3 font-medium text-muted-foreground hover:text-foreground transition-colors">
          {t('billing.invoices')}
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="text-sm text-muted-foreground mb-1">{t('billing.totalClients')}</div>
          <div className="text-3xl font-bold">{clients.length}</div>
        </div>
        <div className="card">
          <div className="text-sm text-muted-foreground mb-1">{t('billing.activeSubscriptions')}</div>
          <div className="text-3xl font-bold text-green-600">
            {subscriptions.filter(s => s.status === 'active').length}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-muted-foreground mb-1">{t('billing.suspendedSubscriptions')}</div>
          <div className="text-3xl font-bold text-yellow-600">
            {subscriptions.filter(s => s.status === 'suspended').length}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-muted-foreground mb-1">{t('billing.totalMrr')}</div>
          <div className="text-3xl font-bold text-primary">
            R$ {subscriptions
              .filter(s => s.status === 'active')
              .reduce((acc, s) => acc + (s.billingCycle === 'monthly' ? s.planPrice : s.planPrice / 12), 0)
              .toFixed(2)}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-border">
        <button
          className={`pb-3 px-4 font-medium transition-colors ${
            activeTab === 'clients'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('clients')}
        >
          {t('billing.clientsTab', { count: clients.length })}
        </button>
        <button
          className={`pb-3 px-4 font-medium transition-colors ${
            activeTab === 'subscriptions'
              ? 'text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('subscriptions')}
        >
          {t('billing.subscriptionsTab', { count: subscriptions.length })}
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex items-center justify-between">
            <div>
              <strong>{t('common.error')}:</strong> {error}
              <div className="text-sm mt-1">{t('billing.demoData')}</div>
            </div>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">×</button>
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            placeholder={activeTab === 'clients' ? t('billing.searchClientsPlaceholder') : t('billing.searchSubscriptionsPlaceholder')}
            className="input pl-10 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Clients Table */}
      {activeTab === 'clients' && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('billing.clientColumn')}</th>
                  <th>{t('billing.typeColumn')}</th>
                  <th>{t('billing.documentColumn')}</th>
                  <th>{t('billing.registrationDate')}</th>
                  <th>{t('common.status')}</th>
                  <th className="text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.id}>
                    <td>
                      <div>
                        <div className="font-medium">{client.name}</div>
                        <div className="text-sm text-muted-foreground">{client.email}</div>
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-outline">
                        {client.type === 'company' ? t('billing.company') : t('billing.individual')}
                      </span>
                    </td>
                    <td className="font-mono text-sm">{client.document}</td>
                    <td>{client.createdAt ? new Date(client.createdAt).toLocaleDateString('pt-BR') : '-'}</td>
                    <td>{getStatusBadge(client.status)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleViewClient(client)} title="Visualizar">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEditClient(client)} title="Editar">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Ban className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Subscriptions Table */}
      {activeTab === 'subscriptions' && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('billing.clientColumn')}</th>
                  <th>{t('billing.plans')}</th>
                  <th>{t('billing.amount')}</th>
                  <th>{t('billing.cycleColumn')}</th>
                  <th>{t('billing.startColumn')}</th>
                  <th>{t('billing.usageColumn')}</th>
                  <th>{t('common.status')}</th>
                  <th className="text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.map((sub) => (
                  <tr key={sub.id}>
                    <td className="font-medium">{sub.clientName}</td>
                    <td>
                      <span className="badge badge-primary badge-outline">
                        {sub.planName}
                      </span>
                    </td>
                    <td className="font-semibold">
                      R$ {sub.planPrice.toFixed(2)}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        {sub.billingCycle === 'monthly' ? t('billing.monthly') : t('billing.annual')}
                      </div>
                    </td>
                    <td>{new Date(sub.startDate).toLocaleDateString('pt-BR')}</td>
                    <td>
                      <div className="text-sm">
                        <div>{t('billing.companiesCountLabel', { count: sub.companiesCount })}</div>
                        <div className="text-muted-foreground">{t('billing.usersCountLabel', { count: sub.usersCount })}</div>
                      </div>
                    </td>
                    <td>{getStatusBadge(sub.status)}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleViewSubscription(sub)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleEditSubscription(sub)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {sub.status === 'active' ? (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleSuspendSubscription(sub.id)}
                            title="Suspender assinatura"
                          >
                            <Ban className="w-4 h-4 text-yellow-500" />
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleActivateSubscription(sub.id)}
                            title="Ativar assinatura"
                          >
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Modals */}
      <ClientModal
        isOpen={showClientModal}
        onClose={() => {
          setShowClientModal(false);
          setEditingClient(null);
        }}
        onSave={handleSaveClient}
        client={editingClient}
      />

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          setEditingSubscription(null);
        }}
        onSave={handleSaveSubscription}
        subscription={editingSubscription}
        clients={clients.filter((c) => c.id).map(c => ({ id: c.id as string, name: c.name }))}
        plans={plans}
      />

      {/* Modal de Visualização */}
      {viewingClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setViewingClient(null)}>
          <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border flex justify-between items-center">
              <h2 className="text-xl font-semibold">{t('billing.clientDetails')}</h2>
              <button onClick={() => setViewingClient(null)} className="text-muted-foreground hover:text-foreground">
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('common.name')}</label>
                  <p className="text-base">{viewingClient.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.typeColumn')}</label>
                  <p className="text-base">{viewingClient.type === 'company' ? t('billing.company') : t('billing.individual')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('common.email')}</label>
                  <p className="text-base">{viewingClient.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.document')}</label>
                  <p className="text-base">{viewingClient.document}</p>
                </div>
                {viewingClient.phone && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">{t('common.phone')}</label>
                    <p className="text-base">{viewingClient.phone}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('common.status')}</label>
                  <p className="text-base">{getStatusBadge(viewingClient.status)}</p>
                </div>
                {viewingClient.address && (
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">{t('billing.address')}</label>
                    <p className="text-base">{viewingClient.address}</p>
                  </div>
                )}
                {viewingClient.city && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">{t('billing.city')}</label>
                    <p className="text-base">{viewingClient.city} - {viewingClient.state}</p>
                  </div>
                )}
                {viewingClient.zipCode && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">{t('billing.zipCode')}</label>
                    <p className="text-base">{viewingClient.zipCode}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.registrationDateLabel')}</label>
                  <p className="text-base">{new Date(viewingClient.createdAt!).toLocaleDateString('pt-BR')}</p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-border flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setViewingClient(null)}>
                {t('common.close')}
              </Button>
              <Button onClick={() => {
                setEditingClient(viewingClient);
                setViewingClient(null);
                setShowClientModal(true);
              }}>
                <Edit className="w-4 h-4 mr-2" />
                {t('common.edit')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Assinatura */}
      {viewingSubscription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setViewingSubscription(null)}>
          <div className="bg-background rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border flex justify-between items-center">
              <h2 className="text-xl font-semibold">{t('billing.subscriptionDetails')}</h2>
              <button onClick={() => setViewingSubscription(null)} className="text-muted-foreground hover:text-foreground">
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.clientColumn')}</label>
                  <p className="text-base">{viewingSubscription.clientName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('common.status')}</label>
                  <p className="text-base">{getStatusBadge(viewingSubscription.status)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.plans')}</label>
                  <p className="text-base">{viewingSubscription.planName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.amount')}</label>
                  <p className="text-base">R$ {viewingSubscription.planPrice.toFixed(2).replace('.', ',')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.billingCycleLabel')}</label>
                  <p className="text-base">{viewingSubscription.billingCycle === 'monthly' ? t('billing.monthly') : t('billing.annual')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.autoRenew')}</label>
                  <p className="text-base">{viewingSubscription.autoRenew ? t('common.yes') : t('common.no')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.subscriptionStartDate')}</label>
                  <p className="text-base">{new Date(viewingSubscription.startDate).toLocaleDateString('pt-BR')}</p>
                </div>
                {viewingSubscription.endDate && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">{t('billing.subscriptionEndDate')}</label>
                    <p className="text-base">{new Date(viewingSubscription.endDate).toLocaleDateString('pt-BR')}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.companies')}</label>
                  <p className="text-base">{viewingSubscription.companiesCount} empresa(s)</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">{t('billing.users')}</label>
                  <p className="text-base">{viewingSubscription.usersCount} usuário(s)</p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-border flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setViewingSubscription(null)}>
                Fechar
              </Button>
              <Button onClick={() => {
                setEditingSubscription(viewingSubscription);
                setViewingSubscription(null);
                setShowSubscriptionModal(true);
              }}>
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sistema de Alertas */}
      <AlertContainer alerts={alerts} onRemove={removeAlert} />
      
      {/* Modal de Confirmação */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        confirmVariant={confirmDialog.confirmVariant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
        isLoading={confirmLoading}
      />
    </div>
  );
}
